import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconTrashOff,
  IconCalendar,
  IconFilter,
  IconSearch,
  IconUser,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
const currency = "";
import { useOrderDeletionReport } from "../controllers/reports.controller";
import ReportExportWrapper from "../components/ReportExportWrapper";

export default function OrderDeletionReportPage() {
  const { t } = useTranslation();

  const filterTypeRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();

  const [state, setState] = useState({
    filter: "today",
    fromDate: null,
    toDate: null,
    search: ""
  });

  const { data, isLoading, error } = useOrderDeletionReport({
    dateFilter: state.filter,
    from: state.fromDate,
    to: state.toDate,
  });

  const filters = [
    { key: "today", value: "Today" },
    { key: "yesterday", value: "Yesterday" },
    { key: "last_7days", value: "Last 7 Days" },
    { key: "this_month", value: "This Month" },
    { key: "last_month", value: "Last Month" },
    { key: "custom", value: "Custom Date" },
  ];

  if (isLoading) return <Page><div className="flex items-center justify-center h-full"><span className="loading loading-spinner loading-lg text-restro-green"></span></div></Page>;
  if (error) return <Page>Error loading report.</Page>;

  let reportData = data?.report || [];
  
  if (state.search) {
    reportData = reportData.filter(item => 
      item.token_no?.toString().includes(state.search) || 
      item.created_by?.toLowerCase().includes(state.search.toLowerCase()) ||
      item.cancelled_by?.toLowerCase().includes(state.search.toLowerCase()) ||
      item.cancel_reason?.toLowerCase().includes(state.search.toLowerCase()) ||
      item.items?.some(it => it.toLowerCase().includes(state.search.toLowerCase()))
    );
  }

  const columns = [
    { header: "Order/Token", key: "order_id" },
    { header: "Type", key: "type" },
    { header: "Date", key: "date" },
    { header: "Created By", key: "created_by" },
    { header: "Cancelled By", key: "cancelled_by" },
    { header: "Table", key: "table_title" },
    { header: "Items & Value Loss", key: "total_value" },
    { header: "Reason", key: "cancel_reason" },
  ];

  const tableData = reportData.map(item => ({
    order_id: `Order #${item.order_id} (Token: ${item.token_no || '-'})`,
    type: item.cancellation_type === 'item_only' ? 'Item Cancelled' : 'Full Order Cancelled',
    date: new Date(item.cancelled_at || item.date).toLocaleString(),
    created_by: item.created_by || "System",
    cancelled_by: item.cancelled_by || "Unknown",
    table_title: item.table_title || "Walk-in",
    total_value: item.total_value,
    cancel_reason: item.cancel_reason
  }));

  const summaryData = {
    "Total Cancelled Orders": data?.summary?.totalCancelledOrders || 0,
    "Total Cancelled Items": data?.summary?.totalCancelledItems || 0,
    "Total Value Lost": currency + (data?.summary?.totalLossAmount || 0).toFixed(2),
  };

  return (
    <Page>
      <ReportExportWrapper
        title="Order Deletion Report"
        reportType="Operational Reports"
        dateRange={filters.find(f => f.key === state.filter)?.value}
        filters={`Search: ${state.search || 'None'}`}
        columns={columns}
        tableData={tableData}
        summaryData={summaryData}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4 print:hidden">
          <div>
            <h3 className="text-3xl font-black tracking-tight flex items-center gap-2 text-red-600">
              <IconTrashOff size={32} /> Order Deletion Report
            </h3>
            <p className="text-gray-500 text-sm mt-1">Audit trail of cancelled orders and waiter reasons</p>
          </div>

          <div className="flex gap-2">
              <button
                  onClick={() => document.getElementById("filter-dialog").showModal()}
                  className="btn btn-ghost bg-restro-gray border border-restro-border-green rounded-2xl gap-2"
              >
                  <IconFilter stroke={iconStroke} size={20} /> {t("reports.filters_button")}
              </button>
          </div>
        </div>

      <div className="flex mb-6 bg-white dark:bg-restro-gray p-2 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm max-w-md">
        <div className="flex items-center px-4 text-gray-400">
            <IconSearch size={20} />
        </div>
        <input 
            type="text" 
            placeholder="Search by waiter, reason, or token..." 
            className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-3 outline-none"
            value={state.search}
            onChange={(e) => setState({...state, search: e.target.value})}
        />
      </div>

      <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-[2rem] overflow-hidden shadow-sm">
        <div className="overflow-x-auto print:overflow-visible">
          <table className="table table-zebra w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 print:bg-transparent border-b">
              <tr>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Order Info</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Waiters</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Table</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Items & Value</th>
                <th className="font-bold text-xs uppercase tracking-widest p-6 text-gray-400 print:text-black print:p-2">Cancellation Reason</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-20 opacity-50 italic">No cancelled orders found for this period.</td>
                </tr>
              )}
              {reportData.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition print:hover:bg-transparent">
                  <td className="p-6 font-bold print:p-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-red-600">Order #{item.order_id}</span>
                      {item.cancellation_type === 'item_only' ? (
                        <span className="badge badge-warning badge-sm font-semibold text-[10px]">Item Cancelled</span>
                      ) : (
                        <span className="badge badge-error badge-sm text-white font-semibold text-[10px]">Full Order</span>
                      )}
                    </div>
                    <div className="text-gray-500 font-medium text-xs">Token: {item.token_no || '-'}</div>
                    <div className="text-gray-400 font-medium text-[10px] mt-1">{new Date(item.cancelled_at || item.date).toLocaleString()}</div>
                  </td>
                  <td className="p-6 text-sm print:text-black print:p-2">
                    <div className="flex flex-col gap-1">
                      <div className="text-gray-500 font-medium text-xs">Created: <span className="text-gray-800 dark:text-gray-200">{item.created_by || "System"}</span></div>
                      <div className="text-red-600 font-medium text-xs flex items-center gap-1">Cancelled: 
                        <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center text-[8px] font-bold text-red-600 uppercase print:hidden">
                            {item.cancelled_by?.[0] || <IconUser size={10}/>}
                        </div>
                        {item.cancelled_by || "Unknown"}
                      </div>
                      <div className="text-gray-400 font-medium text-[10px]">{item.cancelled_at ? new Date(item.cancelled_at).toLocaleString() : ''}</div>
                    </div>
                  </td>
                  <td className="p-6 font-medium print:p-2">{item.table_title || "Walk-in"}</td>
                  <td className="p-6 print:p-2">
                    <div className="text-xs text-gray-500 mb-1 max-w-[150px] truncate" title={item.items.join(', ')}>{item.items.join(', ')}</div>
                    <div className="font-black text-gray-800 dark:text-gray-200">{currency}{item.total_value.toFixed(2)}</div>
                  </td>
                  <td className="p-6 print:p-2">
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-2xl text-xs border border-red-100 dark:border-red-900/30 max-w-xs italic print:bg-transparent print:border-none print:text-black print:p-0">
                        "{item.cancel_reason || "No reason provided"}"
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className = 'modal-box border border-restro-border-green dark:rounded-3xl p-0 overflow-hidden'>
          <div className="bg-gray-900 p-8 text-white">
            <h3 className = "font-black text-2xl flex items-center gap-2">
                <IconFilter stroke={2} /> {t("reports.filter")}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Select date range for report</p>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.filter_label")}</label>
              <select
                className='select select-bordered w-full rounded-2xl bg-gray-50 dark:bg-gray-900 border-none'
                ref={filterTypeRef}
                defaultValue={state.filter}
              >
                {filters.map((filter, index) => (
                  <option key={index} value={filter.key}>
                    {filter.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.from")}</label>
                <input
                  type="date"
                  ref={fromDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.to")}</label>
                <input
                  type="date"
                  ref={toDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button onClick={() => document.getElementById("filter-dialog").close()} className="flex-1 py-4 font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-2xl transition active:scale-95">{t("reports.close")}</button>
              <button
                onClick={() => {
                  setState({
                    ...state,
                    filter: filterTypeRef.current.value,
                    fromDate: fromDateRef.current.value || null,
                    toDate: toDateRef.current.value || null,
                  });
                  document.getElementById("filter-dialog").close();
                }}
                className = 'flex-[1.5] py-4 font-bold text-white bg-restro-green rounded-2xl hover:bg-restro-green-button-hover transition active:scale-95 shadow-lg shadow-green-100 dark:shadow-none'
              >
                {t("reports.apply")}
              </button>
            </div>
          </div>
        </div>
      </dialog>
      </ReportExportWrapper>
    </Page>
  );
}
